// Catalog v2 domain contracts and pure rules for Omni Phase 1 (#1619).
// Settled in 2026-10-17 review plan. Money is always integer cents. Timestamps are ISO 8601 strings.

import type { Billing, Cents, DealerModel, Iso, OneBillMapping } from "./index.js";
import { exactName } from "./customers-v2.js";

/** OneBill code without a trailing "-PHNW"/"-Retail" (spaces allowed around the dash). Nothing else stripped. Case kept. Moved here from quote-intakes.ts. */
export function onebillCodeStem(code: string | null | undefined): string {
 if (!code) return "";
 const s = code.trim();
 const stripped = s.replace(/\s*-\s*(phnw|retail)\b.*$/i, "");
 if (stripped !== s && stripped.length > 0) return stripped.trim();
 return s;
}

/** Both non-blank and equal after trim + uppercase. */
export function sameProductCode(a: string | null | undefined, b: string | null | undefined): boolean {
 const aTrim = a ? a.trim() : "";
 const bTrim = b ? b.trim() : "";
 if (!aTrim || !bTrim) return false;
 return aTrim.toUpperCase() === bTrim.toUpperCase();
}

export type CatalogCodeSource = "autotask" | "kqm";

export function codeSourceOf(p: Pick<CatalogProductV2, "code" | "codeSource">): CatalogCodeSource | null {
 return p.codeSource ?? (p.code?.trim() ? "autotask" : null);
}

export interface KqmTitleProduct {
 title: string | null;
 manufacturerPartNumber: string | null;
}

export type KqmTitleIndex = ReadonlyMap<string, readonly KqmTitleProduct[]>; // key exactName(title)

export function indexKqmProductsByTitle(products: readonly KqmTitleProduct[]): KqmTitleIndex {
 const map = new Map<string, KqmTitleProduct[]>();
 for (const p of products) {
  if (!p.title) continue;
  const key = exactName(p.title);
  if (!key) continue;
  const existing = map.get(key) ?? [];
  existing.push(p);
  map.set(key, existing);
 }
 return map;
}

export interface CatalogCodeInput {
 name: string;
 current: { code: string; codeSource: CatalogCodeSource | null };
 autotask: { code: string; name: string } | null; // null: no Autotask item this run
 kqm: KqmTitleIndex | null; // null: KQM not read this run
}

export interface CatalogCodeResult {
 code: string;
 codeSource: CatalogCodeSource | null;
 conflict: { autotask: string; kqm: string[] } | null;
}

export function resolveCatalogCode(input: CatalogCodeInput): CatalogCodeResult {
 // 1. If autotask is null, return current unchanged.
 if (input.autotask === null) {
  return {
   code: input.current.code,
   codeSource: input.current.codeSource,
   conflict: null,
  };
 }

 // 2. Build kqmCodes: the distinct trimmed, non-blank part numbers of index entries for exactName(name) and exactName(autotask.name). Use [] if kqm is null.
 const kqmCodes: string[] = [];
 if (input.kqm !== null) {
  const seen = new Set<string>();
  const addFromKey = (rawName: string) => {
   const key = exactName(rawName);
   if (!key) return;
   const entries = input.kqm?.get(key) ?? [];
   for (const entry of entries) {
    const part = entry.manufacturerPartNumber ? entry.manufacturerPartNumber.trim() : "";
    if (part && !seen.has(part)) {
     seen.add(part);
     kqmCodes.push(part);
    }
   }
  };
  addFromKey(input.name);
  addFromKey(input.autotask.name);
 }

 const atCode = input.autotask.code ? input.autotask.code.trim() : "";

 // 3. If the Autotask code is non-blank, return it with source "autotask". Set conflict when kqmCodes is non-empty and none of them equals it.
 if (atCode) {
  const hasEqual = kqmCodes.some((kc) => kc === atCode);
  const conflict = (kqmCodes.length > 0 && !hasEqual) ? { autotask: atCode, kqm: kqmCodes } : null;
  return {
   code: atCode,
   codeSource: "autotask",
   conflict,
  };
 }

 // 4. If kqm is null: keep current when its source is "kqm", else return ""/null.
 if (input.kqm === null) {
  if (input.current.codeSource === "kqm") {
   return {
    code: input.current.code,
    codeSource: "kqm",
    conflict: null,
   };
  }
  return {
   code: "",
   codeSource: null,
   conflict: null,
  };
 }

 // 5. If kqmCodes has exactly one entry, return it with source "kqm".
 if (kqmCodes.length === 1) {
  return {
   code: kqmCodes[0]!,
   codeSource: "kqm",
   conflict: null,
  };
 }

 // 6. If it has more than one, return ""/null with conflict { autotask: "", kqm }.
 if (kqmCodes.length > 1) {
  return {
   code: "",
   codeSource: null,
   conflict: { autotask: "", kqm: kqmCodes },
  };
 }

 // 7. Otherwise return ""/null.
 return {
  code: "",
  codeSource: null,
  conflict: null,
 };
}
export type AutotaskSyncState =
 | "in_sync"
 | "creating"
 | "push_failed"
 | "changed_in_autotask"
 | "missing_in_autotask";

export type AutotaskPeriod =
 | "one_time"
 | "monthly"
 | "quarterly"
 | "semi_annual"
 | "yearly"
 | null;

export type AutotaskDifferenceField =
 | "name"
 | "description"
 | "listPrice"
 | "unitCost"
 | "active"
 | "category"
 | "billing";

export interface AutotaskDifference {
 field: AutotaskDifferenceField;
 omni: string;
 autotask: string;
}

export interface AutotaskSync {
 state: AutotaskSyncState;
 period: AutotaskPeriod;
 checkedAt: Iso | null;
 pushedAt: Iso | null;
 error: string | null;
 differences: AutotaskDifference[];
}

export type BillingLinkState = "unreviewed" | "linked" | "create" | "not_billed";

export interface CatalogBillingLink {
 state: BillingLinkState;
 via?: "ops" | "auto" | null;
 tier?: "code" | "exact_name" | "product_key" | null;
 reviewedBy: string | null;
 reviewedAt: Iso | null;
 copyFrom?: OneBillMapping | null;
 createError?: { at: Iso; by: string; message: string } | null;
}
/**
 * CatalogProduct v2 shape per Section 3.3 (lens_catalog/{id}).
 * Existing ids kept (e.g. "service-33"), new ids are "p-<uuid>".
 */
export interface CatalogProductV2 {
 id: string;
 code: string;
 codeSource?: CatalogCodeSource | null;
 name: string;
 description: string;
 category: string; // a name, never a picklist number
 billing: Billing | null; // null: Autotask period Omni cannot quote (quarterly, semi-annual, yearly)
 unit: string;
 listPrice: Cents;
 unitCost: Cents | null;
 active: boolean; // replaces autotaskActive; Omni's, mirrored to Autotask isActive
 floorPrice: Cents;
 wholesaleCost: Cents | null;
 enabled: boolean;
 autotask: { kind: "service" | "product"; id: number } | null; // null only while Omni creates it
 autotaskSync: AutotaskSync;
 onebill: OneBillMapping | null;
 billingLink: CatalogBillingLink;
 createdBy: string | null;
 createdAt: Iso;
 updatedAt: Iso;

 // Backwards compatibility for consumers before 1B/1C migration:
 autotaskActive?: boolean;
 syncedFromAutotaskAt?: Iso;
}

/**
 * lens_catalog_dismissals/{"autotask:product-123" | "onebill:<productCode>"}
 */
export interface CatalogDismissal {
 source: "autotask" | "onebill";
 ref: string;
 name: string;
 by: string;
 at: Iso;
}

export type CatalogDismissalInput = Omit<CatalogDismissal, "at"> & { at?: Iso };

/**
 * lens_onebill_products/{productCode} (snapshot; refreshed when older than 24h or on ops request)
 */
export interface OneBillProductSnapshot {
 productCode: string;
 name: string;
 status: string;
 plans: Array<{
  id: string;
  code: string;
  name: string;
  billing: Billing | "other";
  amount: Cents | null;
  active: boolean;
  glCode: string | null;
 }>;
 plansError: string | null;
 readAt: Iso;
}

export interface ProductInput {
 name: string;
 description: string;
 code: string;
 category: string;
 billing: Billing;
 unit: string;
 listPrice: Cents;
 unitCost: Cents | null;
 floorPrice: Cents;
 wholesaleCost: Cents | null;
 enabled: boolean;
 active?: boolean;
 onebill?: { productCode: string; planId: string };
}

export interface CatalogPatch {
  name?: string;
  description?: string;
  code?: string;
  category?: string;
  unit?: string;
  listPrice?: Cents;
  unitCost?: Cents | null;
  active?: boolean;
  floorPrice?: Cents;
  wholesaleCost?: Cents | null;
  enabled?: boolean;
  expectedUpdatedAt: Iso;
}

export type AutotaskAction = "retry" | "keep_omni" | "take_autotask" | "create_again";

export interface AutotaskActionRequest {
 action: AutotaskAction;
}

export interface AutotaskSyncPrecondition {
 expectedUpdatedAt?: Iso;
 expectedState?: AutotaskSyncState | AutotaskSyncState[];
}

export const REASON_BILLS_YEARLY = "Bills yearly in Autotask";

export function newCatalogProductId(uuid: string): string {
 return `p-${uuid}`;
}

export function isCatalogProductId(id: string): boolean {
 return id.startsWith("p-") || id.startsWith("service-") || id.startsWith("product-");
}

/**
 * Product key per Section 3.2:
 * - Lowercase, then drop a trailing "-PHNW"/"-Retail" suffix.
 * - Turn "&" into "and", split on anything that is not a letter or digit, sort the words and join them with spaces.
 */
export function productKey(name: string): string {
 let s = name.toLowerCase().trim();
 s = s.replace(/\s*-\s*(phnw|retail)\b.*$/i, "");
 s = s.replace(/&/g, " and ");
 const words = s.split(/[^a-z0-9]+/).filter(Boolean);
 return words.sort().join(" ");
}

export interface DerivedBillingResult {
 billing: Billing | null;
 period: AutotaskPeriod;
 reason?: string;
}

/**
 * Derives billing period and quoter billing from Autotask periodType.
 * Measured in Section 2 and 3.4:
 * - Products: 1 One-Time, 2 Monthly, 3 Quarterly, 5 Yearly. (Empty/null is One-Time).
 * - Services: 2 Monthly, 3 Quarterly, 4 Semi-Annual, 5 Yearly.
 * Monthly and one-time can be quoted; yearly, quarterly, and semi-annual cannot.
 * Yearly services get no billing and the reason "Bills yearly in Autotask".
 */
export function deriveBillingFromPeriod(
 periodType: number | string | null | undefined,
 _kind?: "service" | "product",
): DerivedBillingResult {
 if (periodType === null || periodType === undefined || periodType === "") {
  return { billing: "one_time", period: "one_time" };
 }

 if (typeof periodType === "string") {
  const trimmed = periodType.trim();
  if (!trimmed) {
   return { billing: "one_time", period: "one_time" };
  }
  const num = Number(trimmed);
  if (!Number.isNaN(num)) {
   return deriveBillingFromPeriod(num);
  }
  const norm = trimmed.toLowerCase().replace(/[-_ ]/g, "");
  if (norm === "onetime" || norm === "onetime") {
   return { billing: "one_time", period: "one_time" };
  }
  if (norm === "monthly") {
   return { billing: "monthly", period: "monthly" };
  }
  if (norm === "quarterly") {
   return { billing: null, period: "quarterly", reason: "Bills quarterly in Autotask" };
  }
  if (norm === "semiannual") {
   return { billing: null, period: "semi_annual", reason: "Bills semi-annually in Autotask" };
  }
  if (norm === "yearly" || norm === "annual") {
   return { billing: null, period: "yearly", reason: REASON_BILLS_YEARLY };
  }
  return { billing: null, period: null, reason: `Unsupported period ${periodType}` };
 }

 switch (periodType) {
  case 1:
   return { billing: "one_time", period: "one_time" };
  case 2:
   return { billing: "monthly", period: "monthly" };
  case 3:
   return { billing: null, period: "quarterly", reason: "Bills quarterly in Autotask" };
  case 4:
   return { billing: null, period: "semi_annual", reason: "Bills semi-annually in Autotask" };
  case 5:
   return { billing: null, period: "yearly", reason: REASON_BILLS_YEARLY };
  default:
   return { billing: null, period: null, reason: `Unknown Autotask periodType ${periodType}` };
 }
}

export type CanEnableRefusalReason =
 | "no_billing"
 | "inactive"
 | "missing_autotask_link"
 | "autotask_not_in_sync"
 | "autotask_active_differs"
 | "missing_onebill_link"
 | "missing_wholesale_cost"
 | "disabled";

export interface CanEnableResult {
 ok: boolean;
 code?: CanEnableRefusalReason;
 reason?: string;
}

/**
 * Checks whether a catalog product can be enabled or quoted.
 * Section 4 1B:
 * "quoting needs billing set, active, an Autotask link that is in sync
 * (or changed in Autotask with no difference in active), a OneBill link,
 * and a wholesale cost for wholesale dealers. Enable is refused unless these hold."
 */
export function canEnable(
 product: Pick<
  CatalogProductV2,
  "billing" | "active" | "autotask" | "autotaskSync" | "billingLink" | "onebill" | "wholesaleCost"
 >,
 options?: { model?: DealerModel; wholesale?: boolean },
): CanEnableResult {
 // 1. Billing set
 if (!product.billing) {
  return {
   ok: false,
   code: "no_billing",
   reason: REASON_BILLS_YEARLY,
  };
 }

 // 2. Active
 if (!product.active) {
  return {
   ok: false,
   code: "inactive",
   reason: "Product is inactive",
  };
 }

 // 3. Autotask link
 if (!product.autotask) {
  return {
   ok: false,
   code: "missing_autotask_link",
   reason: "Missing Autotask link",
  };
 }

 // 4. Autotask sync state
 if (product.autotaskSync) {
  const { state, differences } = product.autotaskSync;
  if (state === "creating" || state === "push_failed" || state === "missing_in_autotask") {
   return {
    ok: false,
    code: "autotask_not_in_sync",
    reason: `Autotask link is not in sync (${state})`,
   };
  }
  if (state === "changed_in_autotask") {
   const activeDiff = (differences ?? []).some((d) => d.field === "active");
   if (activeDiff) {
    return {
     ok: false,
     code: "autotask_active_differs",
     reason: "Autotask active status differs from Omni",
    };
   }
  }
 }

 // 5. OneBill link
 if (product.billingLink?.state !== "linked" || !product.onebill) {
  return {
   ok: false,
   code: "missing_onebill_link",
   reason: "Product is not linked to OneBill",
  };
 }

 // 6. Wholesale cost for wholesale dealers
 const isWholesale = options?.model === "wholesale" || options?.wholesale === true;
 if (isWholesale && product.wholesaleCost === null) {
  return {
   ok: false,
   code: "missing_wholesale_cost",
   reason: "Product has no wholesale cost set",
  };
 }

 return { ok: true };
}

export function checkProductQuotableV2(
 product: Pick<
  CatalogProductV2,
  "enabled" | "billing" | "active" | "autotask" | "autotaskSync" | "billingLink" | "onebill" | "wholesaleCost"
 >,
 dealer?: { model: DealerModel },
): CanEnableResult {
 if (!product.enabled) {
  return {
   ok: false,
   code: "disabled",
   reason: "Product is disabled",
  };
 }
 return canEnable(product, { model: dealer?.model });
}

export type QuotableBlockerCode =
 | "not_billed"
 | "disabled"
 | "no_billing"
 | "inactive"
 | "missing_autotask_link"
 | "autotask_not_in_sync"
 | "autotask_active_differs"
 | "missing_onebill_link"
 | "missing_wholesale_cost";

export interface QuotableBlocker {
 code: QuotableBlockerCode;
 reason: string;
}

const AUTOTASK_OUT_OF_SYNC = new Set(["creating", "push_failed", "missing_in_autotask"]);

/**
 * Every reason a dealer cannot quote this product, in the order
 * checkProductQuotable uses, then canEnable. The first reason matches that
 * function. missing_onebill_link is omitted when the product is not billed
 * through OneBill, because that state already says so.
 */
export function quotableBlockers(
 product: Pick<
  CatalogProductV2,
  "name" | "enabled" | "billing" | "active" | "autotask" | "autotaskSync" | "billingLink" | "onebill" | "wholesaleCost"
 >,
 dealer: { model: DealerModel },
): QuotableBlocker[] {
 const blockers: QuotableBlocker[] = [];
 const notBilled = product.billingLink?.state === "not_billed";
 if (notBilled) {
  blockers.push({ code: "not_billed", reason: "Not billed through OneBill." });
 }
 if (!product.enabled) {
  blockers.push({ code: "disabled", reason: `Product "${product.name}" is disabled.` });
 }
 if (!product.billing) {
  blockers.push({ code: "no_billing", reason: REASON_BILLS_YEARLY });
 }
 if (!product.active) {
  blockers.push({ code: "inactive", reason: `Product "${product.name}" is inactive in Autotask.` });
 }
 if (!product.autotask) {
  blockers.push({ code: "missing_autotask_link", reason: "Missing Autotask link" });
 } else if (product.autotaskSync) {
  const { state, differences } = product.autotaskSync;
  if (AUTOTASK_OUT_OF_SYNC.has(state)) {
   blockers.push({
    code: "autotask_not_in_sync",
    reason: `Autotask link is not in sync (${state})`,
   });
  } else if (state === "changed_in_autotask" && (differences ?? []).some((d) => d.field === "active")) {
   blockers.push({
    code: "autotask_active_differs",
    reason: "Autotask active status differs from Omni",
   });
  }
 }
 if (!notBilled && (product.billingLink?.state !== "linked" || !product.onebill)) {
  blockers.push({ code: "missing_onebill_link", reason: "Not linked to a OneBill product yet." });
 }
 if (dealer.model === "wholesale" && product.wholesaleCost === null) {
  blockers.push({
   code: "missing_wholesale_cost",
   reason: `Product "${product.name}" has no wholesale buy price set.`,
  });
 }
 return blockers;
}

/** Link and enable can be done on the review screen. Everything else is elsewhere. */
export function inlineFixes(blockers: readonly QuotableBlocker[]): {
 link: boolean;
 enable: boolean;
 elsewhere: QuotableBlocker[];
} {
 return {
  link: blockers.some((blocker) => blocker.code === "missing_onebill_link"),
  enable: blockers.some((blocker) => blocker.code === "disabled"),
  elsewhere: blockers.filter(
   (blocker) => blocker.code !== "missing_onebill_link" && blocker.code !== "disabled",
  ),
 };
}


export interface MatchableProduct {
 id: string;
 name: string;
 code?: string;
 billing: Billing | null;
 billingLinkState?: BillingLinkState;
 reviewedBy?: string | null;
 onebill?: OneBillMapping | null;
}

export interface MatchableOneBillProduct {
 code: string;
 name: string;
 plans: Array<{
  id: string;
  code: string;
  name: string;
  billing: Billing | "other";
  amount: Cents | null;
  active: boolean;
 }>;
}

export interface ProductMatchResult {
 productId: string;
 productCode: string;
 pricePlanId: string;
 tier: "code" | "exact_name" | "product_key";
}

export type CodeLinkOutcome = { productId: string; code: string } & (
 | { kind: "linked_now"; productCode: string; pricePlanId: string }
 | { kind: "linked_same"; productCode: string }
 | { kind: "link_differs"; linked: string; byCode: string | null }
 | { kind: "reviewed"; productCode: string }
 | { kind: "no_onebill" }
 | { kind: "ambiguous"; productCodes: string[] }
 | { kind: "no_plan"; productCode: string; reason: "billing_differs" | "several_plans" | "no_amount" | "no_billing" }
);

export interface OneBillLinkPlan {
 matches: ProductMatchResult[];
 codes: CodeLinkOutcome[];
}

export interface CodeReportProduct {
 id: string;
 name: string;
}

export interface CatalogCodeReport {
 kqm: { read: boolean; products: number; error: string | null };
 withCode: { autotask: number; kqm: number };
 linkedByCode: number; // billingLink.state linked && tier code
 noOneBill: Array<{ code: string; products: CodeReportProduct[] }>;
 ambiguous: Array<{ code: string; productCodes: string[]; products: CodeReportProduct[] }>;
 noPlan: Array<{ code: string; productCode: string; reason: "billing_differs" | "several_plans" | "no_amount" | "no_billing"; products: CodeReportProduct[] }>;
 linkDiffers: Array<{ code: string; linked: string; byCode: string | null; product: CodeReportProduct; via: "ops" | "auto" | null }>;
 reviewed: Array<{ code: string; productCode: string; product: CodeReportProduct }>;
 shared: Array<{ code: string; products: CodeReportProduct[] }>; // code on >1 Omni product
 conflicts: Array<{ autotask: string; kqm: string[]; product: CodeReportProduct }>;
}

/**
 * Product to OneBill matching rule (Section 4):
 * matchProductsToOneBill takes every catalog product and decides eligibility itself:
 * - not_billed products: skipped, no outcome.
 * - Coded products, already linked: linked_same if the link is the code's product, else link_differs. Never changed.
 * - Coded products, not linked: OneBill candidates are the products whose stem equals the code.
 *   - 0 candidates -> no_onebill; more than 1 -> ambiguous.
 *   - The plan must be the only active plan with the product's billing and a non-null amount. If there is no such plan -> no_plan with the reason.
 *   - Not eligible (reviewedBy set, or state other than unreviewed) -> reviewed.
 *   - Otherwise -> match with tier "code". Several products may link to one OneBill product.
 * - Name tiers: run exactly as today, but only over eligible products with a blank code. OneBill products already linked or matched by code are excluded.
 */
export function matchProductsToOneBill(
 omniProducts: MatchableProduct[],
 onebillProducts: MatchableOneBillProduct[],
 alreadyLinkedProductIds: Set<string> = new Set(),
 alreadyLinkedOneBillCodes: Set<string> = new Set(),
): OneBillLinkPlan {
 const matches: ProductMatchResult[] = [];
 const codes: CodeLinkOutcome[] = [];

 // Map OneBill products by stem
 const obByStem = new Map<string, MatchableOneBillProduct[]>();
 for (const ob of onebillProducts) {
  const stem = onebillCodeStem(ob.code).toUpperCase();
  if (!stem) continue;
  const list = obByStem.get(stem) ?? [];
  list.push(ob);
  obByStem.set(stem, list);
 }

 const matchedOmniIds = new Set<string>(alreadyLinkedProductIds);
 const matchedObCodes = new Set<string>(alreadyLinkedOneBillCodes);

 function inspectOneBillPlan(
  ob: MatchableOneBillProduct,
  billing: Billing | null,
 ): { ok: true; planId: string } | { ok: false; reason: "billing_differs" | "several_plans" | "no_amount" | "no_billing" } {
  if (billing === null) {
   return { ok: false, reason: "no_billing" };
  }
  const activePlans = ob.plans.filter((pl) => pl.active);
  const matchingBilling = activePlans.filter((pl) => pl.billing === billing);
  if (matchingBilling.length === 0) {
   return { ok: false, reason: "billing_differs" };
  }
  if (matchingBilling.length > 1) {
   return { ok: false, reason: "several_plans" };
  }
  const plan = matchingBilling[0]!;
  if (plan.amount === null) {
   return { ok: false, reason: "no_amount" };
  }
  return { ok: true, planId: plan.id };
 }

 const codedProducts: MatchableProduct[] = [];
 const uncodedProducts: MatchableProduct[] = [];

 for (const p of omniProducts) {
  // not_billed products: skipped, no outcome.
  if (p.billingLinkState === "not_billed") {
   continue;
  }
  const c = p.code ? p.code.trim() : "";
  if (c) {
   codedProducts.push(p);
  } else {
   uncodedProducts.push(p);
  }
 }

 // Process coded products
 for (const p of codedProducts) {
  const c = p.code!.trim();
  const cUpper = c.toUpperCase();
  const obCandidates = obByStem.get(cUpper) ?? [];

  // Coded products, already linked: linked_same if the link is the code's product, else link_differs. Never changed.
  const isAlreadyLinked = Boolean(p.onebill || alreadyLinkedProductIds.has(p.id) || p.billingLinkState === "linked");
  if (isAlreadyLinked) {
   const linkedCode = p.onebill?.productCode ?? "";
   const linkedStem = onebillCodeStem(linkedCode).toUpperCase();
   const byCodeCandidate = obCandidates.length === 1 ? obCandidates[0]!.code : (obCandidates.find((ob) => ob.code.toUpperCase() === cUpper)?.code ?? (obCandidates[0]?.code ?? null));

   if (linkedStem === cUpper || (linkedCode && linkedCode.toUpperCase() === cUpper)) {
    codes.push({
     productId: p.id,
     code: c,
     kind: "linked_same",
     productCode: linkedCode,
    });
   } else {
    codes.push({
     productId: p.id,
     code: c,
     kind: "link_differs",
     linked: linkedCode,
     byCode: byCodeCandidate,
    });
   }
   continue;
  }

  // Coded products, not linked:
  if (obCandidates.length === 0) {
   codes.push({
    productId: p.id,
    code: c,
    kind: "no_onebill",
   });
   continue;
  }

  if (obCandidates.length > 1) {
   codes.push({
    productId: p.id,
    code: c,
    kind: "ambiguous",
    productCodes: obCandidates.map((ob) => ob.code),
   });
   continue;
  }

  const ob = obCandidates[0]!;
  const planInspection = inspectOneBillPlan(ob, p.billing);
  if (!planInspection.ok) {
   codes.push({
    productId: p.id,
    code: c,
    kind: "no_plan",
    productCode: ob.code,
    reason: planInspection.reason,
   });
   continue;
  }

  // Not eligible (reviewedBy set, or state other than unreviewed) -> reviewed.
  const isReviewed = Boolean(p.reviewedBy || (p.billingLinkState && p.billingLinkState !== "unreviewed"));
  if (isReviewed) {
   codes.push({
    productId: p.id,
    code: c,
    kind: "reviewed",
    productCode: ob.code,
   });
   continue;
  }

  // Otherwise -> match with tier "code". Several products may link to one OneBill product.
  matches.push({
   productId: p.id,
   productCode: ob.code,
   pricePlanId: planInspection.planId,
   tier: "code",
  });
  codes.push({
   productId: p.id,
   code: c,
   kind: "linked_now",
   productCode: ob.code,
   pricePlanId: planInspection.planId,
  });
  matchedOmniIds.add(p.id);
  matchedObCodes.add(ob.code);
 }

 // Name tiers: run exactly as today, but only over eligible products with a blank code. OneBill products already linked or matched by code are excluded.
 const eligibleOmni = uncodedProducts.filter((p) => {
  if (matchedOmniIds.has(p.id)) return false;
  if (p.onebill !== null && p.onebill !== undefined) return false;
  if (p.billing === null) return false;
  if (p.reviewedBy) return false;
  if (p.billingLinkState && p.billingLinkState !== "unreviewed") return false;
  return true;
 });

 const eligibleOb = onebillProducts.filter((ob) => !matchedObCodes.has(ob.code));

 function getEligiblePlan(ob: MatchableOneBillProduct, billing: Billing): { id: string } | null {
  const matchingPlans = ob.plans.filter(
   (pl) => pl.active && pl.billing === billing && pl.amount !== null,
  );
  if (matchingPlans.length === 1) {
   return { id: matchingPlans[0]!.id };
  }
  return null;
 }

 // Tier 1: Exact Name
 const omniByExact = new Map<string, MatchableProduct[]>();
 for (const p of eligibleOmni) {
  const k = exactName(p.name);
  const list = omniByExact.get(k) ?? [];
  list.push(p);
  omniByExact.set(k, list);
 }

 const obByExact = new Map<string, MatchableOneBillProduct[]>();
 for (const ob of eligibleOb) {
  const k = exactName(ob.name);
  const list = obByExact.get(k) ?? [];
  list.push(ob);
  obByExact.set(k, list);
 }

 const ambiguousExactKeys = new Set<string>();
 for (const [k, pList] of omniByExact.entries()) {
  const obList = obByExact.get(k);
  if (pList.length > 1 || (obList && obList.length > 1)) {
   ambiguousExactKeys.add(k);
  }
 }
 for (const [k, obList] of obByExact.entries()) {
  if (obList.length > 1) {
   ambiguousExactKeys.add(k);
  }
 }

 for (const [k, pList] of omniByExact.entries()) {
  if (ambiguousExactKeys.has(k)) continue;
  const obList = obByExact.get(k);
  if (!obList || obList.length !== 1) continue;
  const omni = pList[0]!;
  const ob = obList[0]!;

  const plan = getEligiblePlan(ob, omni.billing!);
  if (plan) {
   matches.push({
    productId: omni.id,
    productCode: ob.code,
    pricePlanId: plan.id,
    tier: "exact_name",
   });
   matchedOmniIds.add(omni.id);
   matchedObCodes.add(ob.code);
  }
 }

 // Tier 2: Product Key (Near-exact)
 const omniForTier2 = eligibleOmni.filter(
  (p) => !matchedOmniIds.has(p.id) && !ambiguousExactKeys.has(exactName(p.name)),
 );
 const obForTier2 = eligibleOb.filter(
  (ob) => !matchedObCodes.has(ob.code) && !ambiguousExactKeys.has(exactName(ob.name)),
 );

 const omniByKey = new Map<string, MatchableProduct[]>();
 for (const p of omniForTier2) {
  const k = productKey(p.name);
  if (!k) continue;
  const list = omniByKey.get(k) ?? [];
  list.push(p);
  omniByKey.set(k, list);
 }

 const obByKey = new Map<string, MatchableOneBillProduct[]>();
 for (const ob of obForTier2) {
  const k = productKey(ob.name);
  if (!k) continue;
  const list = obByKey.get(k) ?? [];
  list.push(ob);
  obByKey.set(k, list);
 }

 const ambiguousProductKeys = new Set<string>();
 for (const [k, pList] of omniByKey.entries()) {
  const obList = obByKey.get(k);
  if (pList.length > 1 || (obList && obList.length > 1)) {
   ambiguousProductKeys.add(k);
  }
 }
 for (const [k, obList] of obByKey.entries()) {
  if (obList.length > 1) {
   ambiguousProductKeys.add(k);
  }
 }

 for (const [k, pList] of omniByKey.entries()) {
  if (ambiguousProductKeys.has(k)) continue;
  const obList = obByKey.get(k);
  if (!obList || obList.length !== 1) continue;
  const omni = pList[0]!;
  const ob = obList[0]!;

  const plan = getEligiblePlan(ob, omni.billing!);
  if (plan) {
   matches.push({
    productId: omni.id,
    productCode: ob.code,
    pricePlanId: plan.id,
    tier: "product_key",
   });
   matchedOmniIds.add(omni.id);
   matchedObCodes.add(ob.code);
  }
 }

 return { matches, codes };
}

export function buildCatalogCodeReport(input: {
 products: CatalogProductV2[];
 codes: CodeLinkOutcome[];
 conflicts: Array<{ productId: string; autotask: string; kqm: string[] }>;
 kqm: CatalogCodeReport["kqm"];
}): CatalogCodeReport {
 const productMap = new Map<string, CatalogProductV2>();
 for (const p of input.products) {
  productMap.set(p.id, p);
 }

 const toRef = (id: string, fallbackName?: string): CodeReportProduct => {
  const p = productMap.get(id);
  return { id, name: p?.name ?? fallbackName ?? id };
 };

 let autotaskCount = 0;
 let kqmCount = 0;
 let linkedByCode = 0;

 const byCodeOmni = new Map<string, CodeReportProduct[]>();

 for (const p of input.products) {
  const src = codeSourceOf(p);
  if (src === "autotask") autotaskCount++;
  else if (src === "kqm") kqmCount++;

  if (p.billingLink?.state === "linked" && p.billingLink.tier === "code") {
   linkedByCode++;
  }

  const c = p.code ? p.code.trim() : "";
  if (c) {
   const list = byCodeOmni.get(c) ?? [];
   list.push({ id: p.id, name: p.name });
   byCodeOmni.set(c, list);
  }
 }

 const noOneBillMap = new Map<string, CodeReportProduct[]>();
 const ambiguousMap = new Map<string, { productCodes: string[]; products: CodeReportProduct[] }>();
 const noPlanMap = new Map<string, { productCode: string; reason: "billing_differs" | "several_plans" | "no_amount" | "no_billing"; products: CodeReportProduct[] }>();
 const linkDiffersList: CatalogCodeReport["linkDiffers"] = [];
 const reviewedList: CatalogCodeReport["reviewed"] = [];

 for (const outcome of input.codes) {
  const ref = toRef(outcome.productId);
  if (outcome.kind === "no_onebill") {
   const list = noOneBillMap.get(outcome.code) ?? [];
   list.push(ref);
   noOneBillMap.set(outcome.code, list);
  } else if (outcome.kind === "ambiguous") {
   const existing = ambiguousMap.get(outcome.code) ?? { productCodes: outcome.productCodes, products: [] };
   existing.products.push(ref);
   ambiguousMap.set(outcome.code, existing);
  } else if (outcome.kind === "no_plan") {
   const key = `${outcome.code}:${outcome.productCode}:${outcome.reason}`;
   const existing = noPlanMap.get(key) ?? { productCode: outcome.productCode, reason: outcome.reason, products: [] };
   existing.products.push(ref);
   noPlanMap.set(key, existing);
  } else if (outcome.kind === "link_differs") {
   const prod = productMap.get(outcome.productId);
   linkDiffersList.push({
    code: outcome.code,
    linked: outcome.linked,
    byCode: outcome.byCode,
    product: ref,
    via: prod?.billingLink?.via ?? null,
   });
  } else if (outcome.kind === "reviewed") {
   reviewedList.push({
    code: outcome.code,
    productCode: outcome.productCode,
    product: ref,
   });
  }
 }

 const noOneBill = Array.from(noOneBillMap.entries()).map(([code, products]) => ({
  code,
  products: products.sort((a, b) => a.name.localeCompare(b.name)),
 })).sort((a, b) => a.code.localeCompare(b.code));

 const ambiguous = Array.from(ambiguousMap.entries()).map(([code, val]) => ({
  code,
  productCodes: val.productCodes,
  products: val.products.sort((a, b) => a.name.localeCompare(b.name)),
 })).sort((a, b) => a.code.localeCompare(b.code));

 const noPlan = Array.from(noPlanMap.values()).map((val) => {
  const firstProd = val.products[0];
  const prod = firstProd ? productMap.get(firstProd.id) : undefined;
  const code = prod?.code?.trim() || "";
  return {
   code,
   productCode: val.productCode,
   reason: val.reason,
   products: val.products.sort((a, b) => a.name.localeCompare(b.name)),
  };
 }).sort((a, b) => a.code.localeCompare(b.code));

 linkDiffersList.sort((a, b) => a.code.localeCompare(b.code) || a.product.name.localeCompare(b.product.name));
 reviewedList.sort((a, b) => a.code.localeCompare(b.code) || a.product.name.localeCompare(b.product.name));

 const shared: CatalogCodeReport["shared"] = [];
 for (const [code, prods] of byCodeOmni.entries()) {
  if (prods.length > 1) {
   shared.push({
    code,
    products: prods.sort((a, b) => a.name.localeCompare(b.name)),
   });
  }
 }
 shared.sort((a, b) => a.code.localeCompare(b.code));

 const conflicts: CatalogCodeReport["conflicts"] = input.conflicts.map((c) => ({
  autotask: c.autotask,
  kqm: c.kqm,
  product: toRef(c.productId),
 })).sort((a, b) => a.autotask.localeCompare(b.autotask) || a.product.name.localeCompare(b.product.name));

 return {
  kqm: input.kqm,
  withCode: { autotask: autotaskCount, kqm: kqmCount },
  linkedByCode,
  noOneBill,
  ambiguous,
  noPlan,
  linkDiffers: linkDiffersList,
  reviewed: reviewedList,
  shared,
  conflicts,
 };
}

/**
 * Catalog route table per Section 3.6:
 *
 * Catalog (Phase 1):
 *   POST   /api/catalog                         ProductInput            -> CatalogProduct (or 502 push_failed)
 *   POST   /api/catalog/from-autotask          {kind, id}               -> CatalogProduct
 *   PATCH  /api/catalog/:id                     CatalogPatch            -> CatalogProduct (or 409 if stale)
 *   POST   /api/catalog/:id/autotask           AutotaskActionRequest    -> CatalogProduct
 *   GET    /api/catalog/onebill/:productCode/omni-candidates            -> Array<{ productId: string; name: string; score: number }>
 *   POST   /api/catalog/dismissals             CatalogDismissalInput    -> CatalogDismissal
 *   DELETE /api/catalog/dismissals/:key                                 -> 200 {}
 *
 * Note: POST /api/catalog/refresh is removed.
 */
export const CATALOG_ROUTES = {
 create: { method: "POST", path: "/api/catalog" },
 fromAutotask: { method: "POST", path: "/api/catalog/from-autotask" },
 patch: { method: "PATCH", path: "/api/catalog/:id" },
 autotaskAction: { method: "POST", path: "/api/catalog/:id/autotask" },
 candidatesForOneBill: { method: "GET", path: "/api/catalog/onebill/:productCode/omni-candidates" },
 listDismissals: { method: "GET", path: "/api/catalog/dismissals" },
 createDismissal: { method: "POST", path: "/api/catalog/dismissals" },
 deleteDismissal: { method: "DELETE", path: "/api/catalog/dismissals/:key" },
} as const;
